<template>
    <!-- A modal dialog over the page (filters, Create Itinerary). Named by the
         first heading its content renders — "Filter Sites", "Create My
         Itinerary" — so callers don't each have to pass a label. -->
    <div ref="dialogEl" class="flyout" role="dialog" aria-modal="true" :aria-labelledby="labelledBy" :aria-label="labelledBy ? undefined : 'Filters'" tabindex="-1">
        <div class="flyout-inner">
            <div class="container">
                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script setup>
    const emit = defineEmits(['toggleFilters']);

    function toggleFilters() {
        emit('toggleFilters');
    }

    defineExpose({
        toggleFilters,
    });

    const props = defineProps({
        // Pass it when the flyout is kept mounted and shown with v-show; omit
        // it when the flyout is rendered with v-if, and it is open for as long
        // as it is mounted. `default: undefined` keeps "omitted" distinguishable
        // from false, which a Boolean prop otherwise collapses it to.
        showing: {
            type: Boolean,
            required: false,
            default: undefined,
        },
    });

    const { showing } = toRefs(props);
    const dialogEl = ref(null);
    const labelledBy = ref(undefined);
    const headingId = useId();

    // With no `showing`, a flyout is open if it was mounted visible (the v-if
    // case). One mounted hidden by v-show without `showing` can't be tracked,
    // so it is never treated as open — pass `showing` for that.
    const mountedVisible = ref(false);
    onMounted(() => {
        mountedVisible.value = dialogEl.value?.style.display !== 'none';
    });
    const isOpen = () => (showing.value === undefined ? mountedVisible.value : showing.value);

    // Focus in, Tab kept inside, Escape closes (Asana O2-28), scroll locked,
    // focus back to the Filters button on close.
    useDialog(dialogEl, { active: isOpen, onEscape: toggleFilters });

    function nameFromHeading() {
        const heading = dialogEl.value?.querySelector('h1, h2, h3');
        if (!heading) {
            labelledBy.value = undefined;
            return;
        }
        if (!heading.id) heading.id = headingId;
        labelledBy.value = heading.id;
    }

    watch(isOpen, (open) => {
        if (open) nextTick(nameFromHeading);
    });

    watch(showing, () => {
        if (showing.value === true) {
            if (!document.body.classList.contains('hide-bottom-nav')) {
                document.body.classList.add('hide-bottom-nav');
            }
        } else {
            if (document.body.classList.contains('hide-bottom-nav')) {
                document.body.classList.remove('hide-bottom-nav');
            }
        }
    });

    onMounted(() => {
        if (isOpen()) nameFromHeading();
    });
</script>

<style lang="scss" scoped>
    .flyout {
        -webkit-overflow-scrolling: touch;

        // Focus lands on the dialog itself when it opens.
        &:focus {
            outline: none;
        }

        position: fixed;
        left: 0;
        top: 0;
        padding-top: 59px;
        height: 100svh;
        width: 100%;
        overflow: hidden;
        z-index: $zStepFive;

        &::before {
            content: '';
            display: inline-block;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 59px;
            background: $gray-darkest;
            background-blend-mode: multiply;
            opacity: 0.7;

            @media (min-width: $tablet) {
                width: 100%;
                height: 100%;
            }
        }

        .flyout-inner {
            // z-index: $zStepOne;
            overflow: auto;
            position: absolute;
            background-color: white;
            top: $header_height;
            left: 0;
            width: 100%;
            height: calc(100svh - $header_height);
            will-change: transform;

            transform: translateY(0%);
            transition: transform 0.3s ease-in-out;

            @starting-style {
                transform: translateY(100%);
                transition: transform 0.3s ease-in-out;
            }     

            @media (min-width: $tablet) {
                width: 70%;
                max-width: $hd;
                padding-bottom: 98px;
                margin: auto;
                height: 80%;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
            }
        }

        .container {
            background: $white;
            position: relative;
            height: 100%;
            overflow: hidden;
            overflow-y: scroll;

            .back-caret {
                position: absolute;
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                top: 16px;
                left: 16px;
                z-index: 1;
                cursor: pointer;

                @media (min-width: $tablet) {
                    top: 72px;
                    left: 93px;
                }
            }
        }
    }
</style>
